<template>
  <div>
    <v-dialog :modelValue="dialog" max-width="900" @click:outside="close" @keydown.esc="close">
      <v-card>
        <v-card-title class="headline">
          Seleccionar LPNs para imprimir - ASN #{{ asn?.id }}
        </v-card-title>

        <v-card-text>
          <p class="text-body-2 mb-3">
            Seleccione los LPNs que desea imprimir en las etiquetas testigo de recepción.
          </p>

          <div class="mb-3 d-flex justify-space-between align-center">
            <v-btn
              :color="allExpanded ? 'blue-grey' : 'blue-grey'"
              variant="outlined"
              size="small"
              @click="toggleExpandAll"
              class="mr-3"
            >
              <v-icon start>{{ allExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
              {{ allExpanded ? "Contraer Todo" : "Expandir Todo" }}
            </v-btn>

            <v-chip v-if="selected.length > 0" color="primary" variant="elevated">
              {{ selected.length }} LPN{{ selected.length > 1 ? 's' : '' }} seleccionado{{ selected.length > 1 ? 's' : '' }}
            </v-chip>
          </div>

          <v-data-table
            :headers="headers"
            :items="list"
            :search="search"
            :loading="loading"
            loading-text="Cargando..."
            :hide-default-footer="true"
            v-model="selected"
            show-select
            item-value="lpn.id"
            :items-per-page="1000"
          >
            <template v-slot:item="{ item, index, isSelected, toggleSelect }">
              <tr>
                <td>
                  <v-checkbox
                    style="margin: 0px; padding: 0px"
                    hide-details
                    :model-value="isSelected({ value: item.lpn.id })"
                    @update:modelValue="toggleSelect({ value: item.lpn.id })"
                  />
                </td>
                <td>
                  <v-btn variant="text" size="small" @click="toggleExpand(index)" icon>
                    <v-icon>{{ isExpanded(index) ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
                  </v-btn>
                </td>
                <td>{{ item.lpn.code }}</td>
                <td>{{ formatDate(item.lpn.creation_date) }}</td>
                <td class="text-center">{{ item.total_products }}</td>
              </tr>
              <tr v-if="isExpanded(index)" :key="`expanded-${index}`">
                <td :colspan="headers.length" class="pa-0">
                  <div class="pa-4 bg-grey-lighten-5">
                    <h6 class="mb-3">Productos recibidos en este LPN</h6>
                    <div v-for="(reception, rIndex) in item.receptions" :key="rIndex" class="mb-3">
                      <v-card variant="outlined" class="mb-2" style="border-color: #e0e0e0 !important; border-width: 1px">
                        <v-card-text class="py-3">
                          <v-row>
                            <v-col cols="12" md="5">
                              <span class="pb-1 text-caption">Producto</span>
                              <div class="font-weight-medium">{{ reception.product.name }}</div>
                              <div class="text-caption text-grey-600">SKU: {{ reception.product.sku }}</div>
                            </v-col>
                            <v-col cols="12" md="2">
                              <span class="pb-1 text-caption">Cantidad</span>
                              <div class="d-flex align-center font-weight-bold">
                                <div class="text-stock" style="min-width: 0px !important">
                                  {{ reception.base_amount }}
                                </div>
                                <div class="text-caption text-grey-600 flex-shrink-0 mt-1 presentation-type">
                                  {{ reception.presentation_type.name }}
                                </div>
                              </div>
                            </v-col>
                            <v-col cols="12" md="5" v-if="reception.attributes && reception.attributes.length > 0">
                              <v-row class="ma-0">
                                <v-col
                                  cols="6"
                                  class="pt-0"
                                  v-for="(attr, attrIndex) in reception.attributes"
                                  :key="attrIndex"
                                >
                                  <div class="text-caption">{{ attr.label }}</div>
                                  <div class="text-caption font-weight-medium">{{ attr.value }}</div>
                                </v-col>
                              </v-row>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
            <template v-slot:no-data>
              <v-alert type="info" class="mt-3">
                No hay LPNs disponibles para este ASN.
              </v-alert>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click="close()"> Cancelar </v-btn>
          <v-btn
            color="green darken-1"
            text
            @click="confirmSelection()"
            :disabled="selected.length === 0"
          >
            Imprimir {{ selected.length }} Etiqueta{{ selected.length !== 1 ? 's' : '' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { asnService } from "@/libs/ws/asnService";

export default {
  props: {
    dialog: Boolean,
    asn: Object,
  },
  data: () => ({
    list: [],
    loading: false,
    search: "",
    headers: [
      { title: "", align: "center", sortable: false, key: "data-table-select", width: "40px" },
      { title: "", align: "center", sortable: false, key: "expand", width: "60px" },
      { title: "LPN", align: "left", sortable: true, key: "lpn.code" },
      { title: "Fecha Recepción", align: "left", sortable: true, key: "lpn.creation_date" },
      { title: "Total Productos", align: "center", sortable: true, key: "total_products" },
    ],
    expandedRows: [],
    allExpanded: false,
    selected: [],
  }),
  watch: {
    dialog(val) {
      if (val) {
        this.loadList();
      } else {
        this.resetModal();
      }
    },
  },
  methods: {
    loadList() {
      if (!this.asn || !this.asn.id) {
        return;
      }

      this.loading = true;

      asnService
        .getLpnsFromAsn(this.asn.id)
        .then((response) => {
          if (response.status) {
            this.list = response.result.list;
          } else {
            this.$toast.error(response.msg || "Error al cargar los LPNs");
          }
        })
        .catch((e) => {
          console.error("Error loading LPNs:", e);
          this.$toast.error("Error al cargar los LPNs del ASN");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    toggleExpand(index) {
      const expandedIndex = this.expandedRows.indexOf(index);
      if (expandedIndex > -1) {
        this.expandedRows.splice(expandedIndex, 1);
      } else {
        this.expandedRows.push(index);
      }
    },

    isExpanded(index) {
      return this.expandedRows.includes(index);
    },

    toggleExpandAll() {
      if (this.allExpanded) {
        this.expandedRows = [];
      } else {
        this.expandedRows = this.list.map((item, index) => index);
      }
      this.allExpanded = !this.allExpanded;
    },

    confirmSelection() {
      if (this.selected.length === 0) {
        this.$toast.warning("Debe seleccionar al menos un LPN");
        return;
      }

      // Obtener los objetos LPN completos basados en los IDs seleccionados
      const selectedLpns = this.list
        .filter((item) => this.selected.includes(item.lpn.id))
        .map((item) => item.lpn);

      this.$emit("lpnsSelected", selectedLpns);
    },

    close() {
      this.$emit("close");
    },

    resetModal() {
      this.list = [];
      this.selected = [];
      this.expandedRows = [];
      this.allExpanded = false;
      this.search = "";
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.text-stock {
  font-size: 1.1rem;
}
.presentation-type {
  margin-left: 4px;
}
</style>
